import { PostTemplateForManagingFragment } from "generated/schemaTypes";
import { SerializedEditorState } from "lexical";
import { useEffect, useMemo } from "react";
import { DefaultValues, useForm } from "react-hook-form";

import { PostTemplateContentDialogFormData } from "./PostTemplateContentDialogView";
import { PostTemplateSettingsDialogFormData } from "./PostTemplateSettingsDialogView";

type UseContentFormProps = {
  postTemplate?: PostTemplateForManagingFragment;
};

export function useContentForm({ postTemplate }: UseContentFormProps = {}) {
  const defaultValues:
    | DefaultValues<PostTemplateContentDialogFormData>
    | undefined = useMemo(() => {
    if (postTemplate) {
      return {
        title: postTemplate.title || "",
        data: postTemplate.data as SerializedEditorState,
      };
    }

    return undefined;
  }, [postTemplate]);
  const useFormReturn = useForm<PostTemplateContentDialogFormData>({
    defaultValues,
    mode: "onChange",
  });

  /** Update form values when selected template changes */
  useEffect(() => {
    if (postTemplate) {
      useFormReturn.reset(defaultValues);
    }
  }, [postTemplate, defaultValues, useFormReturn]);

  const { formState } = useFormReturn;

  return {
    useFormReturn,
    showConfirmation: formState.isDirty && !formState.isSubmitted,
  };
}

type UseSettingFormProps = {
  postTemplate?: PostTemplateForManagingFragment;
  isDuplicate?: boolean;
};

export function useSettingsForm({
  postTemplate,
  isDuplicate,
}: UseSettingFormProps = {}) {
  const defaultValues:
    | DefaultValues<PostTemplateSettingsDialogFormData>
    | undefined = useMemo(() => {
    if (postTemplate) {
      return {
        departmentId: postTemplate.department?.id || "",
        name: `${postTemplate.name}${isDuplicate ? " (copy)" : ""}`,
      };
    }

    return undefined;
  }, [postTemplate, isDuplicate]);
  const useFormReturn = useForm<PostTemplateSettingsDialogFormData>({
    defaultValues,
    mode: "onChange",
  });

  /** Update form values when selected template changes */
  useEffect(() => {
    if (postTemplate) {
      useFormReturn.reset(defaultValues);
    }
  }, [postTemplate, useFormReturn, defaultValues]);

  const { formState } = useFormReturn;

  return {
    useFormReturn,
    showConfirmation: formState.isDirty && !formState.isSubmitted,
  };
}
